import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import {
  useDelayRender,
  useRemotionEnvironment,
} from 'remotion';
import { parseWebp, ParsedWebp } from './parse-webp';
import { useCurrentWebpIndex } from './use-current-webp-index';
import { getWebpFromCache, setWebpToCache } from './webp-cache';

/**
 * 애니메이션 WebP를 Remotion 프레임과 동기화하여 Canvas에 렌더링하는 컴포넌트.
 * @remotion/gif의 GifForRendering 패턴을 따름.
 *
 * 1. parseWebp()로 모든 프레임 디코딩 (delayRender로 비동기 대기)
 * 2. useCurrentWebpIndex()로 현재 프레임 인덱스 계산
 * 3. Canvas에 ImageData[index] 렌더링
 * 4. 파싱 완료 전/실패 시 빈 Canvas 유지 (@remotion/gif 동일 패턴)
 */

type AnimatedWebpProps = {
  src: string;
  playbackRate?: number;
  style?: React.CSSProperties;
};

const makeCanvas = (): CanvasRenderingContext2D | null => {
  if (typeof document === 'undefined') {
    return null;
  }
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  canvas.width = 0;
  canvas.height = 0;
  return ctx;
};

export const AnimatedWebp = React.forwardRef<
  HTMLCanvasElement,
  AnimatedWebpProps
>(({ src, playbackRate = 1, style }, ref) => {
  const environment = useRemotionEnvironment();
  const {delayRender,continueRender,cancelRender} = useDelayRender();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [tempCtx] = useState(() => makeCanvas());

  const [state, setState] = useState<ParsedWebp>(() => {
    const cached = getWebpFromCache(src);
    if (cached) {
      return cached;
    }
    return { frames: [], delays: [], width: 0, height: 0 };
  });

  // @remotion/gif 패턴: 각 effect run이 독립된 delayRender handle을 가짐
  // React Strict Mode에서 안전하게 동작
  useEffect(() => {
    const cached = getWebpFromCache(src);
    if (cached) {
      setState(cached);
      return;
    }

    const controller = new AbortController();
    let done = false;
    let aborted = false;
    let handleContinued = false;
    const handle = delayRender(`Loading AnimatedWebp with src="${src}"`);

    // continueRender 이중 호출 방지
    const safeContinueRender = () => {
      if (!handleContinued) {
        handleContinued = true;
        continueRender(handle);
      }
    };

    parseWebp(src, controller.signal)
      .then((parsed) => {
        if (aborted) {
          return;
        }
        setWebpToCache(src, parsed);
        setState(parsed);
        done = true;
        safeContinueRender();
      })
      .catch((err: Error) => {
        if (aborted) {
          safeContinueRender();
          return;
        }

        console.error('[AnimatedWebp] parseWebp 실패:', err.message, {
          src: src.slice(0, 120),
        });

        // 렌더링(export) 환경: cancelRender로 렌더 중단
        // preview 환경: render handle 해제하여 복구 가능하게 (src 변경 시 재시도)
        if (environment.isRendering || environment.isClientSideRendering) {
          cancelRender(err);
        } else {
          safeContinueRender();
        }
      });

    return () => {
      if (!done) {
        aborted = true;
        controller.abort();
      }
      safeContinueRender();
    };
  }, [src, environment.isRendering, environment.isClientSideRendering, delayRender, continueRender, cancelRender]);

  const index = useCurrentWebpIndex({
    delays: state.delays,
    playbackRate,
  });

  useImperativeHandle(ref, () => canvasRef.current!, []);

  // Canvas에 ImageData[index] 렌더링
  useEffect(() => {
    const imageData = state.frames[index];
    const ctx = canvasRef.current?.getContext('2d');
    if (!imageData || !tempCtx || !ctx) return;

    if (
      tempCtx.canvas.width < imageData.width ||
      tempCtx.canvas.height < imageData.height
    ) {
      tempCtx.canvas.width = imageData.width;
      tempCtx.canvas.height = imageData.height;
    } else {
      tempCtx.clearRect(0, 0, tempCtx.canvas.width, tempCtx.canvas.height);
    }

    tempCtx.putImageData(imageData, 0, 0);

    const canvas = canvasRef.current;
    if (!canvas) return;

    // fill 모드: 프레임을 Canvas 크기에 맞게 스케일
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      tempCtx.canvas,
      0,
      0,
      imageData.width,
      imageData.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [index, state.frames, tempCtx]);

  // @remotion/gif 패턴: 항상 Canvas만 렌더링
  // 파싱 완료 전/실패 시 빈 Canvas (브라우저의 자체 애니메이션 재생 방지)
  // src 변경(remote → blob) 시 useEffect가 재파싱하여 Canvas에 프레임 렌더링
  return (
    <canvas
      ref={canvasRef}
      width={state.width || undefined}
      height={state.height || undefined}
      style={style}
    />
  );
});

AnimatedWebp.displayName = 'AnimatedWebp';
